// ============================================
// Profile - Manager Module (ES6)
// 프로필 관리자 및 폼 모달
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading, escapeHtml } from '../utils.js';
import { loadProfiles } from './state.js';
import { refreshProfileSelectorList, setPrimaryProfileFromUI } from './selector.js';
import { PROFILE_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCachedPrimaryProfileId } from '../auth/index.js';
import { showConfirm } from '../shared/confirm-modal.js';
import { ModalManager } from '../modal-manager.js';

// ============================================
// 시진 옵션 (사주용)
// ============================================
const BIRTH_TIME_OPTIONS = [
  { value: '모름', label: '모름' },
  { value: '자시', label: '자시 (23:00~01:00)' },
  { value: '축시', label: '축시 (01:00~03:00)' },
  { value: '인시', label: '인시 (03:00~05:00)' },
  { value: '묘시', label: '묘시 (05:00~07:00)' },
  { value: '진시', label: '진시 (07:00~09:00)' },
  { value: '사시', label: '사시 (09:00~11:00)' },
  { value: '오시', label: '오시 (11:00~13:00)' },
  { value: '미시', label: '미시 (13:00~15:00)' },
  { value: '신시', label: '신시 (15:00~17:00)' },
  { value: '유시', label: '유시 (17:00~19:00)' },
  { value: '술시', label: '술시 (19:00~21:00)' },
  { value: '해시', label: '해시 (21:00~23:00)' }
];

function getBirthTimeOptions(selectedValue = '모름') {
  return BIRTH_TIME_OPTIONS.map(opt =>
    `<option value="${opt.value}" ${opt.value === selectedValue ? 'selected' : ''}>${opt.label}</option>`
  ).join('');
}

// ============================================
// Close Profile Manager Modal
// ============================================

export function closeProfileManager() {
  const modal = document.getElementById('profileManagerModal');
  if (!modal) return;

  // 스택에서 먼저 제거 (히스토리 관리)
  ModalManager.remove('profileManagerModal', true);

  // 닫힘 애니메이션 시작
  modal.classList.remove('open');

  // 애니메이션 완료 후 DOM에서 제거 (350ms = 300ms 애니메이션 + 여유)
  setTimeout(() => {
    if (modal.parentNode) {
      modal.remove();
    }
    ModalManager.unregister('profileManagerModal');
  }, 350);
}

// ============================================
// Profile Manager Modal
// ============================================

export async function openProfileManager() {
  showGlobalLoading();
  let profiles = [];
  try {
    profiles = await loadProfiles() || [];
  } catch (e) {
    console.error('[Profile] 프로필 로드 오류:', e);
  } finally {
    hideGlobalLoading();
  }

  const primaryProfileId = getCachedPrimaryProfileId() || null;

  const modal = document.createElement('div');
  modal.className = 'bottom-sheet-overlay';
  modal.id = 'profileManagerModal';
  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="close"></div>
    <div class="bottom-sheet" style="max-height: 80vh;">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-header">
        <h3>프로필 관리</h3>
      </div>
      <div class="bottom-sheet-body">
        <div class="profile-manager-list" id="profileList">
          ${profiles.length === 0 ? `
            <div class="profile-empty">등록된 프로필이 없습니다</div>
          ` : profiles.map(p => {
            const isPrimary = p.id === primaryProfileId;
            return `
            <div class="profile-manager-item ${isPrimary ? 'primary' : ''}" data-id="${p.id}">
              <div class="profile-manager-info">
                <span class="profile-manager-name">${escapeHtml(p.name)}</span>
                ${isPrimary ? '<span class="primary-badge">대표</span>' : ''}
                <span class="profile-manager-gender">${p.gender || '남성'}</span>
              </div>
              <div class="profile-manager-actions">
                ${!isPrimary ? `
                  <button class="set-primary-btn" data-action="setPrimary" data-id="${p.id}" title="대표 프로필로 설정">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </button>
                ` : ''}
                <button class="profile-action-btn" data-action="edit" data-id="${p.id}" title="수정">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button class="profile-action-btn danger" data-action="delete" data-id="${p.id}" title="삭제">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
          `}).join('')}
        </div>
        <button class="profile-add-btn" data-action="addProfile">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          새 프로필 추가
        </button>
      </div>
    </div>
  `;

  // 이벤트 위임 설정
  modal.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    const id = actionEl.dataset.id;

    if (action === 'close') {
      closeProfileManager();
    } else if (action === 'setPrimary') {
      setPrimaryProfileFromUI(id);
    } else if (action === 'edit') {
      editProfile(id);
    } else if (action === 'delete') {
      deleteProfile(id);
    } else if (action === 'addProfile') {
      openProfileFormModal();
    }
  });

  document.body.appendChild(modal);

  requestAnimationFrame(() => {
    modal.classList.add('open');
    if (window.ModalManager) {
      window.ModalManager.register('profileManagerModal', closeProfileManager, 'bottomSheet');
      window.ModalManager.push('profileManagerModal');
    }
  });
}

// ============================================
// Profile Form Modal
// ============================================

export function openProfileFormModal(profileId = null, profileData = null, fromSelector = false) {
  const isEdit = !!profileId;
  const name = profileData?.name || '';
  const gender = profileData?.gender || '남성';
  const birthDate = profileData?.birthDate || '';
  const birthCalendar = profileData?.birthCalendar || '양력';
  const birthTime = profileData?.birthTime || '모름';

  window._profileFormFromSelector = fromSelector;

  const modal = document.createElement('div');
  modal.className = 'bottom-sheet-overlay';
  modal.id = 'profileFormModal';
  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="close"></div>
    <div class="bottom-sheet" style="max-height: 70vh;">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-header">
        <h3>${isEdit ? '프로필 수정' : '새 프로필'}</h3>
      </div>
      <div class="bottom-sheet-body">
        <div class="form-group">
          <label>프로필 이름</label>
          <input type="text" id="profileNameInput" placeholder="예: 김철수, 모험가 등" value="${escapeHtml(name)}">
        </div>
        <div class="form-group">
          <label>성별</label>
          <div class="gender-selector-clean">
            <button type="button" class="gender-btn-clean ${gender === '남성' ? 'selected' : ''}" data-gender="남성" data-action="selectGender">남성</button>
            <button type="button" class="gender-btn-clean ${gender === '여성' ? 'selected' : ''}" data-gender="여성" data-action="selectGender">여성</button>
          </div>
          <input type="hidden" id="profileGenderInput" value="${gender}">
        </div>
        <div class="form-group">
          <label>생년월일</label>
          <div style="display: flex; gap: 10px; align-items: center;">
            <input type="date" id="profileBirthDateInput" value="${birthDate}" style="flex: 1;">
            <select id="profileBirthTimeInput" class="form-select" style="flex: 1;">
              ${getBirthTimeOptions(birthTime)}
            </select>
          </div>
        </div>
        <div class="form-group">
          <label>음력/양력</label>
          <div class="gender-selector-clean">
            <button type="button" class="gender-btn-clean ${birthCalendar === '양력' ? 'selected' : ''}" data-calendar="양력" data-action="selectCalendar">양력</button>
            <button type="button" class="gender-btn-clean ${birthCalendar === '음력' ? 'selected' : ''}" data-calendar="음력" data-action="selectCalendar">음력</button>
          </div>
          <input type="hidden" id="profileBirthCalendarInput" value="${birthCalendar}">
        </div>
        <div style="display: flex; gap: 10px;">
          <button class="btn btn-secondary" data-action="close" style="flex:1;">취소</button>
          <button class="btn btn-primary" data-action="save" style="flex:1;">저장</button>
        </div>
        <input type="hidden" id="editingProfileId" value="${profileId || ''}">
      </div>
    </div>
  `;

  // 이벤트 위임 설정
  modal.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    if (action === 'close') {
      closeProfileFormModal();
    } else if (action === 'save') {
      saveProfile();
    } else if (action === 'selectGender') {
      selectProfileGender(actionEl);
    } else if (action === 'selectCalendar') {
      selectProfileCalendar(actionEl);
    }
  });

  document.body.appendChild(modal);

  requestAnimationFrame(() => {
    modal.classList.add('open');
    if (window.ModalManager) {
      window.ModalManager.register('profileFormModal', closeProfileFormModal, 'bottomSheet');
      window.ModalManager.push('profileFormModal');
    }
  });

  // 모바일에서 키보드 자동 팝업 방지를 위해 focus 제거
}

export function closeProfileFormModal() {
  const modal = document.getElementById('profileFormModal');
  if (!modal) return;

  // 스택에서 먼저 제거 (히스토리 관리)
  ModalManager.remove('profileFormModal', true);

  // 닫힘 애니메이션 시작
  modal.classList.remove('open');

  // 애니메이션 완료 후 DOM에서 제거 (350ms = 300ms 애니메이션 + 여유)
  setTimeout(() => {
    if (modal.parentNode) {
      modal.remove();
    }
    ModalManager.unregister('profileFormModal');
  }, 350);
}

export function selectProfileGender(btn) {
  const container = btn.closest('.gender-selector-clean');
  container.querySelectorAll('.gender-btn-clean').forEach(b => b.classList.remove('selected'));
  btn.classList.add('selected');
  document.getElementById('profileGenderInput').value = btn.dataset.gender;
}

export function selectProfileCalendar(btn) {
  const container = btn.closest('.gender-selector-clean');
  container.querySelectorAll('.gender-btn-clean').forEach(b => b.classList.remove('selected'));
  btn.classList.add('selected');
  document.getElementById('profileBirthCalendarInput').value = btn.dataset.calendar;
}

export async function editProfile(profileId) {
  const profiles = await loadProfiles();
  const profile = profiles.find(p => p.id === profileId);

  if (profile) {
    openProfileFormModal(profileId, profile);
  }
}

export async function saveProfile() {
  const name = document.getElementById('profileNameInput').value.trim();
  const gender = document.getElementById('profileGenderInput').value;
  const birthDate = document.getElementById('profileBirthDateInput').value || null;
  const birthCalendar = document.getElementById('profileBirthCalendarInput').value || '양력';
  const birthTime = document.getElementById('profileBirthTimeInput').value || '모름';
  const editingId = document.getElementById('editingProfileId').value;

  if (!name) {
    showToast('프로필 이름을 입력하세요', 'error');
    return;
  }

  showGlobalLoading();
  try {
    const response = await authFetch(PROFILE_ENDPOINTS.saveProfile, {
      body: {
        profileId: editingId || null,
        name,
        gender,
        birthDate,
        birthCalendar,
        birthTime
      }
    });

    if (!response.ok) throw new Error('서버 오류');

    await loadProfiles(true);
    hideGlobalLoading();

    showToast(editingId ? '프로필이 수정되었습니다' : '프로필이 추가되었습니다');

    closeProfileFormModal();

    if (document.getElementById('profileSelectorModal')) {
      await refreshProfileSelectorList();
    }

    if (document.getElementById('profileManagerModal')) {
      closeProfileManager();
      openProfileManager();
    }
  } catch (e) {
    showToast('저장 오류: ' + e.message, 'error');
    hideGlobalLoading();
  }
}

export async function deleteProfile(profileId) {
  const confirmed = await showConfirm({
    message: '이 프로필을 삭제할까요?',
    confirmText: '삭제',
    isDanger: true
  });
  if (!confirmed) return;

  showGlobalLoading();
  try {
    const response = await authFetch(PROFILE_ENDPOINTS.deleteProfile, {
      body: { profileId }
    });

    if (!response.ok) throw new Error('서버 오류');

    await loadProfiles(true);
    hideGlobalLoading();

    showToast('프로필이 삭제되었습니다');
    closeProfileManager();
    openProfileManager();
  } catch (e) {
    showToast('삭제 오류: ' + e.message, 'error');
    hideGlobalLoading();
  }
}
